import { fetchApiGateway } from "@nova/fetch-api-gateway"
import { useAppSelector } from "integrations/redux/hooks"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import {
    AggregateField,
    castQueryResults,
    InferMultiResult,
    InferQueryResult,
    TimeBucket,
    TypedQueryPayload,
    ValidPath,
} from "@nova/queryable"
import { activityTimelineEventSchema, parseValidItems } from "@nova/schemas"
import useSWR from "swr"

type Resource = "event_logs"

export type ActivityQuery<
    S extends ReadonlyArray<ValidPath<Resource>> | undefined = undefined,
    A extends ReadonlyArray<AggregateField<Resource>> | undefined = undefined,
    T extends ReadonlyArray<TimeBucket<Resource>> | undefined = undefined,
> = TypedQueryPayload<Resource, S, A, T>

export const createActivityQuery = <
    const S extends ReadonlyArray<ValidPath<Resource>> | undefined = undefined,
    const A extends ReadonlyArray<AggregateField<Resource>> | undefined = undefined,
    const T extends ReadonlyArray<TimeBucket<Resource>> | undefined = undefined,
>(
    query: Omit<ActivityQuery<S, A, T>, "resource">
) => ({ ...query, resource: "event_logs" }) as ActivityQuery<S, A, T>

export const fetchActivityQuery = <
    const S extends ReadonlyArray<ValidPath<Resource>> | undefined,
    const A extends ReadonlyArray<AggregateField<Resource>> | undefined,
    const T extends ReadonlyArray<TimeBucket<Resource>> | undefined,
>(
    orgId: string,
    query: ActivityQuery<S, A, T>
): Promise<InferQueryResult<Resource, S, A, T>[]> =>
    (
        fetchApiGateway("post /org/:orgId/activity/query", {
            params: { orgId },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            body: query as any,
        }) as unknown as Promise<Record<string, unknown>[]>
    ).then((rows) => castQueryResults(rows, query) as InferQueryResult<Resource, S, A, T>[])

export type TimelineCursor = { createdAt: string; seenIds: string[] }

export const fetchActivityTimeline = (
    orgId: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    query: ActivityQuery<any, any, any>,
    cursor?: TimelineCursor | null
) =>
    (
        fetchApiGateway("post /org/:orgId/activity/timeline", {
            params: { orgId },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            body: { ...query, cursor: cursor ?? undefined } as any,
        }) as unknown as Promise<{
            events: unknown[]
            rowCount: number
            nextCursor: TimelineCursor | null
        }>
    ).then((response) => ({
        events: parseValidItems(activityTimelineEventSchema, response.events),
        rowCount: response.rowCount,
        nextCursor: response.nextCursor,
    }))

const fetchActivityBatch = <
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const T extends Record<string, ActivityQuery<any, any, any>>,
>(
    orgId: string,
    queries: T
) =>
    fetchApiGateway("post /org/:orgId/activity/batch", {
        params: { orgId },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        body: queries as any,
    }).then((response) => {
        const result: Record<string, unknown[]> = {}
        for (const key of Object.keys(queries)) {
            result[key] = castQueryResults(response[key] ?? [], queries[key])
        }
        return result as InferMultiResult<T>
    })

export function useActivityBatchQuery<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const T extends Record<string, ActivityQuery<any, any, any>>,
>(queries: T) {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { data, isLoading, isValidating, error } = useSWR(
        orgId ? [JSON.stringify(queries), orgId] : null,
        () => fetchActivityBatch(orgId!, queries),
        { keepPreviousData: true }
    )

    return { data, isLoading, isValidating, error }
}
